import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Loader2, Send } from "lucide-react";
import { useState, KeyboardEvent } from "react";

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function ChatInput({
  onSend,
  isLoading = false,
  placeholder = "Ask a question...",
  disabled = false,
}: ChatInputProps) {
  const [input, setInput] = useState("");

  const handleSend = () => {
    const message = input.trim();
    if (message && !isLoading && !disabled) {
      onSend(message);
      setInput("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="relative flex items-center w-full p-4">
      <div className="relative flex w-full items-center rounded-2xl border border-border/50 bg-background shadow-sm focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/50 transition-all duration-200">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading || disabled}
          className="flex-1 border-0 bg-transparent px-5 py-6 text-[15px] shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/70"
        />
        <div className="pr-2">
          <Button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || isLoading || disabled}
            size="icon"
            className={cn(
              "h-10 w-10 rounded-xl transition-all duration-200",
              input.trim() && !isLoading && !disabled
                ? "bg-primary text-primary-foreground shadow-md hover:scale-105"
                : "bg-muted text-muted-foreground"
            )}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4 ml-0.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
